import useFrcCaptcha from '@/hooks/useFrcCaptcha/useFrcCaptcha';
import ButtonSubmit from '@uikit/components/ButtonSubmit/ButtonSubmit';
import { ButtonSubmitState } from '@uikit/components/ButtonSubmit/consts';
import FormField from '@uikit/components/FormField/FormField';
import { FormFieldType } from '@uikit/components/FormField/consts';
import FormFieldRadioGroup from '@uikit/components/FormFieldRadioGroup/FormFieldRadioGroup';
import FormInfoBox from '@uikit/components/FormInfoBox/FormInfoBox';
import FormRow from '@uikit/components/FormRow/FormRow';
import { useState } from 'react';
import delay from '../../../helpers/delay';
import submitContactForm from '../../../helpers/submitContactForm';
import { UseFormState } from '../../../hooks/useForm/interfaces';
import useForm from '../../../hooks/useForm/useForm';
import { FormComponentProps } from '../interfaces';
import { ComfortRentFormValues } from './interfaces';
import schema from './schema';

const initialFormValues: ComfortRentFormValues = {
  targetGroup: 'Wohnungswirtschaft',
  firstName: '',
  lastName: '',
  mail: '',
  phone: '',
  message: '',
};

const ComfortRentForm = ({
  buttonLabel,
  dataProcessing,
  onSuccess,
  ...options
}: FormComponentProps): JSX.Element => {
  const [buttonState, setButtonState] = useState(ButtonSubmitState.Initial);
  const { getSolution, FrcCaptchaComponent } = useFrcCaptcha();

  const callbackOnSubmit = async (formState: UseFormState) => {
    setButtonState(ButtonSubmitState.Pending);

    try {
      const frcCaptchaSolution = await getSolution();
      const response = await submitContactForm(formState, {
        ...options,
        frcCaptchaSolution,
      });
      const json = await response.json();

      if (json.message === 'success') {
        setButtonState(ButtonSubmitState.Success);
        await delay(1500);
        onSuccess && onSuccess();
      } else {
        throw Error;
      }
    } catch (error) {
      setButtonState(ButtonSubmitState.Fail);
      await delay(2000);
      setButtonState(ButtonSubmitState.Initial);
    }
  };

  const {
    formState,
    getErrorByFieldName,
    isFormValid,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useForm(callbackOnSubmit, schema, initialFormValues);

  const FormFieldRadioItems = [
    {
      label: 'Wohnungswirtschaft',
      value: 'Wohnungswirtschaft',
    },
    { label: 'Vermieter', value: 'Vermieter' },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <FormRow>
        <FormFieldRadioGroup
          items={FormFieldRadioItems}
          onChange={handleChange}
          value={formState['targetGroup']}
          name="targetGroup"
        />
      </FormRow>
      <FormRow>
        <FormField
          onBlur={handleBlur}
          onChange={handleChange}
          name="firstName"
          value={formState['firstName']}
          errorMessage={getErrorByFieldName('firstName')}
          label="Ihr Vorname"
          autoComplete="given-name"
          id="first-name"
        />
        <FormField
          onBlur={handleBlur}
          onChange={handleChange}
          name="lastName"
          value={formState['lastName']}
          errorMessage={getErrorByFieldName('lastName')}
          label="Ihr Nachname"
          autoComplete="family-name"
          id="last-name"
        />
      </FormRow>
      <FormRow>
        <FormField
          onBlur={handleBlur}
          onChange={handleChange}
          name="mail"
          value={formState['mail']}
          errorMessage={getErrorByFieldName('mail')}
          label="E-Mail-Adresse"
          autoComplete="email"
          id="email"
        />
        <FormField
          onBlur={handleBlur}
          onChange={handleChange}
          name="phone"
          value={formState['phone']}
          errorMessage={getErrorByFieldName('phone')}
          label="Telefonnummer (optional)"
          autoComplete="tel"
          id="phone"
        />
      </FormRow>
      <FormRow>
        <FormField
          name={'message'}
          value={formState['message']}
          label="Ihre Nachricht an uns (optional)"
          type={FormFieldType.Textarea}
          maxLength={2000}
          onBlur={handleBlur}
          onChange={handleChange}
          errorMessage={getErrorByFieldName('message')}
          id={'message'}
        />
      </FormRow>
      <FormRow>
        <FormInfoBox>{dataProcessing}</FormInfoBox>
      </FormRow>
      <FormRow fullWidthChildren={false} bottomMarginSize={{ default: 1.125 }}>
        <ButtonSubmit
          inactive={!isFormValid}
          state={buttonState}
          id={'submit-contact-form'}
        >
          {buttonLabel}
        </ButtonSubmit>
      </FormRow>

      <FrcCaptchaComponent />
    </form>
  );
};

export default ComfortRentForm;
